import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchActionsInbox } from './actionsInboxApi';
import {
  ORIGIN_LABEL,
  STATUS_LABEL,
  buildOriginPath,
  formatDueDate,
  statusBadgeClass,
} from './actionsInboxUtils';
import type {
  ActionsInboxItem,
  ActionsInboxOrigin,
  ActionsInboxUnifiedStatus,
} from './types';
import { fetchUsers } from '../users/usersApi';
import type { User } from '../users/types';

const ORIGINS = Object.keys(ORIGIN_LABEL) as ActionsInboxOrigin[];
const STATUSES = Object.keys(STATUS_LABEL) as ActionsInboxUnifiedStatus[];

export function ActionsInboxView() {
  const [items, setItems] = useState<ActionsInboxItem[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [status, setStatus] = useState<ActionsInboxUnifiedStatus | ''>('pending');
  const [origin, setOrigin] = useState('');
  const [overdue, setOverdue] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const response = await fetchUsers({ pageSize: 200 });
      setUsers(response.data);
    } catch {
      setUsers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchActionsInbox({
        assignedTo: assignedTo ? Number(assignedTo) : undefined,
        status: (status || undefined) as ActionsInboxUnifiedStatus | undefined,
        origin: (origin || undefined) as ActionsInboxOrigin | undefined,
        overdue: overdue === 'true' ? true : overdue === 'false' ? false : undefined,
      });
      setItems(data);
    } catch {
      setError('No se pudo cargar la bandeja de acciones.');
    } finally {
      setLoading(false);
    }
  }, [assignedTo, status, origin, overdue]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    void load();
  }, [load]);

  const overdueCount = useMemo(() => items.filter((item) => item.isOverdue).length, [items]);

  return (
    <div className='space-y-6' data-testid='actions-inbox'>
      <div>
        <h1 className='text-2xl font-bold text-foreground'>Bandeja de acciones (CAPA)</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Acciones unificadas de no conformidades, riesgos, objetivos, incidentes SST y acuses de
          lectura documental pendientes.
        </p>
      </div>

      <div className='flex flex-wrap gap-3 items-end'>
        <label className='flex flex-col gap-1 text-sm'>
          <span className='text-muted-foreground'>Responsable</span>
          <select
            className='input min-w-[180px]'
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value=''>Todos</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.email}
              </option>
            ))}
          </select>
        </label>

        <label className='flex flex-col gap-1 text-sm'>
          <span className='text-muted-foreground'>Estado</span>
          <select className='input min-w-[140px]' value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value=''>Todos</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABEL[value]}
              </option>
            ))}
          </select>
        </label>

        <label className='flex flex-col gap-1 text-sm'>
          <span className='text-muted-foreground'>Origen</span>
          <select className='input min-w-[180px]' value={origin} onChange={(e) => setOrigin(e.target.value)}>
            <option value=''>Todos</option>
            {ORIGINS.map((value) => (
              <option key={value} value={value}>
                {ORIGIN_LABEL[value]}
              </option>
            ))}
          </select>
        </label>

        <label className='flex flex-col gap-1 text-sm'>
          <span className='text-muted-foreground'>Vencimiento</span>
          <select className='input min-w-[140px]' value={overdue} onChange={(e) => setOverdue(e.target.value)}>
            <option value=''>Todos</option>
            <option value='true'>Vencidas</option>
            <option value='false'>Al día</option>
          </select>
        </label>
      </div>

      {loading && (
        <p className='text-sm text-muted-foreground' data-testid='actions-inbox-loading'>
          Cargando acciones…
        </p>
      )}

      {error && (
        <div className='rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <p className='text-sm text-muted-foreground'>
            {items.length} acción{items.length === 1 ? '' : 'es'}
            {overdueCount > 0 ? ` · ${overdueCount} vencida${overdueCount === 1 ? '' : 's'}` : ''}
          </p>

          <div className='overflow-x-auto rounded-lg border border-border'>
            <table className='min-w-full text-sm'>
              <thead className='bg-muted/50 text-left'>
                <tr>
                  <th className='px-4 py-3 font-medium'>Acción</th>
                  <th className='px-4 py-3 font-medium'>Origen</th>
                  <th className='px-4 py-3 font-medium'>Responsable</th>
                  <th className='px-4 py-3 font-medium'>Vence</th>
                  <th className='px-4 py-3 font-medium'>Estado</th>
                  <th className='px-4 py-3 font-medium'>Ir al origen</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='px-4 py-8 text-center text-muted-foreground'>
                      No hay acciones con los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className='border-t border-border'>
                      <td className='px-4 py-3'>
                        <span className={item.isOverdue ? 'text-destructive font-medium' : ''}>
                          {item.title}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-muted-foreground'>{item.originLabel}</td>
                      <td className='px-4 py-3'>{item.assignedUserName ?? '—'}</td>
                      <td className='px-4 py-3'>
                        <span className={item.isOverdue ? 'text-destructive font-medium' : ''}>
                          {formatDueDate(item.dueDate)}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(item.status)}`}
                        >
                          {STATUS_LABEL[item.status]}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        <Link
                          to={buildOriginPath(item)}
                          className='text-primary hover:underline'
                        >
                          Ver origen
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
